import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Switch,
    FormControlLabel,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsIcon from '@mui/icons-material/Settings';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { useApi } from '../hooks/useApi';

/**
 * Stroomprijs Pagina
 * 
 * Deze pagina toont stroomprijs opbouw met netbeheerder en leverancier tarieven
 * en biedt instellingen voor prijs berekeningen.
 * 
 * Functies:
 * - Tab 1: Prijzengrafiek met breakdown van componenten
 * - Tab 2: Instellingen voor prijsberekening
 * - Kale stroomprijs van Slimverbruik.nl
 * - Staafdiagram voor prijsopbouw (all-in)
 */
const ElectricityPricePage = () => {
    const { t } = useTranslation();
    const { get, post, put } = useApi();
    
    // State management
    const [tabValue, setTabValue] = useState(0);
    const [showBreakdown, setShowBreakdown] = useState(true);
    const [priceData, setPriceData] = useState(null);
    const [settings, setSettings] = useState({
        netbeheerder_tarief: 0.12,
        leverancier_tarief: 0.08,
        show_all_in: true,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch stroomprijs data
    const fetchElectricityPrices = async (customSettings = null) => {
        setLoading(true);
        setError(null);
        
        try {
            let response;
            
            if (customSettings) {
                // POST request met custom settings
                response = await post('/electricity-price/', {
                    settings: customSettings,
                    hours_forward: 72
                });
            } else {
                // GET request met database settings
                response = await get('/electricity-price/?hours_forward=72');
            }

            if (response.data) {
                // Converteer tijdstempels naar moment objecten voor de grafiek
                // Backend stuurt UTC tijden, converteer naar CET/Amsterdam tijd
                const processedData = response.data.data.map(item => ({
                    ...item,
                    timestamp: moment.utc(item.validfrom).tz('Europe/Amsterdam').format('DD-MM HH:mm'),
                    hour: moment.utc(item.validfrom).tz('Europe/Amsterdam').hour(),
                }));

                setPriceData({
                    ...response.data,
                    data: processedData
                });
                
                console.log('Stroomprijs data opgehaald:', response.data);
            }
        } catch (err) {
            console.error('Fout bij ophalen stroomprijs data:', err);
            setError('Kon stroomprijs data niet ophalen. Probeer het opnieuw.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch instellingen uit database
    const fetchSettings = async () => {
        try {
            const response = await get('/electricity-price/settings');
            
            if (response.data) {
                setSettings(response.data);
                console.log('Stroomprijs instellingen opgehaald:', response.data);
            }
        } catch (err) {
            console.error('Fout bij ophalen stroomprijs instellingen:', err);
            // Gebruik default instellingen als er een fout optreedt
            setSettings({
                netbeheerder_tarief: 0.12,
                leverancier_tarief: 0.08,
                show_all_in: true,
            });
        }
    };

    // Update instellingen via electricity-price endpoint
    const updateSettings = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            console.log('VOOR update - Settings naar server:', settings);
            const response = await put('/electricity-price/settings', settings);
            
            if (response.data) {
                console.log('NA update - Server response:', response.data);
                console.log('Verschil gevonden:');
                console.log('- Netbeheerder: verstuur =', settings.netbeheerder_tarief, ', ontvangen =', response.data.netbeheerder_tarief);
                console.log('- Leverancier: verstuur =', settings.leverancier_tarief, ', ontvangen =', response.data.leverancier_tarief);
                
                // Gebruik lokale settings in plaats van server response om overschrijven te voorkomen
                // setSettings(response.data); // COMMENTED OUT - gebruik lokale settings
                setSuccess('Instellingen succesvol bijgewerkt');
                
                console.log('Settings NIET overschreven door server response');
            }
        } catch (err) {
            console.error('Fout bij bijwerken instellingen:', err);
            setError('Kon instellingen niet bijwerken. Probeer het opnieuw.');
        } finally {
            setLoading(false);
        }
    };

    // Export naar CSV functie
    const exportToCSV = () => {
        if (!priceData || !priceData.data) {
            setError('Geen data beschikbaar voor export');
            return;
        }

        const headers = [
            'Tijd',
            'Kale Stroomprijs (€/kWh)',
            'Netbeheerder Tarief (€/kWh)',
            'Leverancier Tarief (€/kWh)',
            'All-in Prijs (€/kWh)',
            'CO2 Emissiefactor (g/kWh)'
        ];

        const csvData = priceData.data.map(item => [
            moment.utc(item.validfrom).tz('Europe/Amsterdam').format('DD-MM-YYYY HH:mm'),
            item.price_raw?.toFixed(4) || '',
            item.price_netbeheerder_tarief?.toFixed(4) || '',
            item.price_leverancier_tarief?.toFixed(4) || '',
            item.price_all_in?.toFixed(4) || '',
            item.emissionfactor?.toFixed(0) || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `stroomprijs_${moment().format('YYYY-MM-DD_HH-mm')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Voorvertoning met aangepaste instellingen
    const previewWithSettings = () => {
        fetchElectricityPrices(settings);
    };

    // Initial data fetch
    useEffect(() => {
        const initializeData = async () => {
            await fetchSettings();
            await fetchElectricityPrices();
        };
        
        initializeData();
    }, []);

    // Handle settings changes
    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: key.includes('tarief') ? parseFloat(value) : value
        }));
    };

    // Breakdown chart data - per uur vanaf vandaag 00:00
    const breakdownData = useMemo(() => {
        if (!priceData?.data || priceData.data.length === 0) return [];
        
        console.log('Breakdown data herberekend met settings:', settings);
        
        // Gebruik alle beschikbare data (vanaf vandaag 00:00) en bereken componenten op basis van settings
        return priceData.data.map(item => {
            const kalePrice = item.price_raw || 0;
            const netbeheerderTarief = settings.netbeheerder_tarief || 0;
            const leverancierTarief = settings.leverancier_tarief || 0;
            const allInPrice = kalePrice + netbeheerderTarief + leverancierTarief;
            
            return {
                time: moment.utc(item.validfrom).tz('Europe/Amsterdam').format('DD-MM HH:mm'),
                hour: moment.utc(item.validfrom).tz('Europe/Amsterdam').hour(),
                'Kale Prijs': kalePrice,
                'Netbeheerder': netbeheerderTarief,
                'Leverancier': leverancierTarief,
                'All-in': allInPrice
            };
        });
    }, [priceData, settings]);

    if (loading && !priceData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Stroomprijs Opbouw & Configuratie
            </Typography>

            {/* Tabs voor verschillende weergaven */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab 
                        icon={<ShowChartIcon />} 
                        label="Prijzen & Grafieken" 
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<SettingsIcon />} 
                        label="Instellingen" 
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {/* Tab 1: Prijzen & Grafieken */}
            {tabValue === 0 && (
                <Box>
                    {/* Controls */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showBreakdown}
                                    onChange={(e) => setShowBreakdown(e.target.checked)}
                                />
                            }
                            label="Toon Prijscomponenten (Leverancier & Netbeheerder)"
                        />
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportToCSV}
                            disabled={!priceData?.data}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                console.log('Current settings:', settings);
                                console.log('Current breakdownData:', breakdownData.slice(0, 3));
                                console.log('First price data item:', priceData?.data?.[0]);
                            }}
                        >
                            Debug Info
                        </Button>
                    </Box>

                    {/* Prijsopbouw staafdiagram */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Stroomprijs Opbouw (vanaf vandaag 00:00)
                            </Typography>
                            <Box sx={{ height: '400px', width: '100%' }}>
                                {breakdownData.length > 0 ? (
                                    <ReactECharts
                                        option={{
                                            title: {
                                                text: 'Stroomprijs Opbouw (vanaf vandaag 00:00)',
                                                left: 'center',
                                                textStyle: {
                                                    fontSize: 14
                                                }
                                            },
                                            tooltip: {
                                                trigger: 'axis',
                                                axisPointer: {
                                                    type: 'cross'
                                                },
                                                formatter: function(params) {
                                                    let result = params[0].axisValue + '<br/>';
                                                    let total = 0;
                                                    
                                                    params.forEach(param => {
                                                        const value = param.value || 0;
                                                        total += value;
                                                        result += `${param.marker} ${param.seriesName}: ${value.toFixed(4)} €/kWh<br/>`;
                                                    });
                                                    
                                                    result += `<br/><strong>Totaal: ${total.toFixed(4)} €/kWh</strong>`;
                                                    return result;
                                                }
                                            },
                                            legend: {
                                                data: showBreakdown 
                                                    ? ['Leverancier', 'Netbeheerder', 'Kale Prijs', 'All-in Prijs']
                                                    : ['Kale Prijs', 'All-in Prijs'],
                                                top: 30
                                            },
                                            grid: {
                                                left: '3%',
                                                right: '4%',
                                                bottom: '3%',
                                                containLabel: true
                                            },
                                            xAxis: {
                                                type: 'category',
                                                data: breakdownData.map(item => item.time),
                                                axisLabel: {
                                                    rotate: 45,
                                                    interval: Math.ceil(breakdownData.length / 16) // Toon ongeveer 16 labels voor betere leesbaarheid
                                                }
                                            },
                                            yAxis: {
                                                type: 'value',
                                                name: 'Prijs (€/kWh)',
                                                nameLocation: 'middle',
                                                nameGap: 40,
                                                axisLabel: {
                                                    formatter: '{value}'
                                                }
                                            },
                                            series: [
                                                {
                                                    name: 'Leverancier',
                                                    type: 'bar',
                                                    data: showBreakdown ? breakdownData.map(item => item['Leverancier']) : [],
                                                    itemStyle: {
                                                        color: '#FF9800',
                                                        opacity: showBreakdown ? 1 : 0
                                                    },
                                                    stack: 'constant',
                                                    emphasis: {
                                                        focus: 'series'
                                                    }
                                                },
                                                {
                                                    name: 'Netbeheerder',
                                                    type: 'bar',
                                                    data: showBreakdown ? breakdownData.map(item => item['Netbeheerder']) : [],
                                                    itemStyle: {
                                                        color: '#4CAF50',
                                                        opacity: showBreakdown ? 1 : 0
                                                    },
                                                    stack: 'constant',
                                                    emphasis: {
                                                        focus: 'series'
                                                    }
                                                },
                                                {
                                                    name: 'Kale Prijs',
                                                    type: 'line',
                                                    data: breakdownData.map(item => item['Kale Prijs']),
                                                    itemStyle: {
                                                        color: '#2196F3'
                                                    },
                                                    lineStyle: {
                                                        width: 3
                                                    },
                                                    symbol: 'circle',
                                                    symbolSize: 6,
                                                    emphasis: {
                                                        focus: 'series'
                                                    }
                                                },
                                                {
                                                    name: 'All-in Prijs',
                                                    type: 'line',
                                                    data: breakdownData.map(item => item['All-in']),
                                                    itemStyle: {
                                                        color: '#F44336'
                                                    },
                                                    lineStyle: {
                                                        width: 3,
                                                        type: 'dashed'
                                                    },
                                                    symbol: 'diamond',
                                                    symbolSize: 8,
                                                    emphasis: {
                                                        focus: 'series'
                                                    }
                                                }
                                            ]
                                        }}
                                        style={{ height: '100%', width: '100%' }}
                                    />
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Geen prijsopbouw data beschikbaar
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Tab 2: Instellingen */}
            {tabValue === 1 && (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        Stroomprijs Configuratie
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {/* Basis instellingen */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Prijscomponenten (€/kWh)
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Netbeheerder Tarief"
                                                type="number"
                                                step="0.001"
                                                value={settings.netbeheerder_tarief}
                                                onChange={(e) => handleSettingChange('netbeheerder_tarief', e.target.value)}
                                                helperText="Tarief voor netbeheer en transport"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Leverancier Tarief"
                                                type="number"
                                                step="0.001"
                                                value={settings.leverancier_tarief}
                                                onChange={(e) => handleSettingChange('leverancier_tarief', e.target.value)}
                                                helperText="Marge en kosten van energieleverancier"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Weergave instellingen */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="subtitle1" gutterBottom>
                                        Weergave Instellingen
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.show_all_in}
                                                        onChange={(e) => handleSettingChange('show_all_in', e.target.checked)}
                                                    />
                                                }
                                                label="Toon All-in Prijs in Grafieken"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Action buttons */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={previewWithSettings}
                                    disabled={loading}
                                >
                                    Voorvertoning
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={updateSettings}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : null}
                                >
                                    {loading ? 'Opslaan...' : 'Opslaan en Toepassen'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Snackbars voor feedback */}
            <Snackbar
                open={!!success}
                autoHideDuration={4000}
                onClose={() => setSuccess(null)}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ElectricityPricePage; 
 